import { useEffect, useState, useCallback } from 'react';
import './HomePage.css';
import heroMainVideo from './assets/videos/main_video.mp4';
import { API_BASE } from './config';

// Format date from various formats to "Month DD, YYYY" (e.g., "July 20, 2026")
function formatDateDisplay(dateString) {
  if (!dateString) return dateString;
  
  try {
    // Remove any whitespace
    const trimmed = String(dateString).trim();
    
    // Check if already in "Month DD, YYYY" format
    if (/^[A-Za-z]+\s+\d{1,2},\s+\d{4}$/.test(trimmed)) {
      console.log('[HomePage] formatDateDisplay: already formatted: ' + trimmed);
      return trimmed;
    }

    // Try YYYY-MM-DD format first (ISO 8601)
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const [year, month, day] = trimmed.split('-');
      const date = new Date(`${year}-${month}-${day}T00:00:00Z`);
      if (!isNaN(date.getTime())) {
        const formatted = new Intl.DateTimeFormat('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }).format(date);
        console.log('[HomePage] formatDateDisplay: ISO format ' + trimmed + ' → ' + formatted);
        return formatted;
      }
    }

    // Try parsing as any date string (handles various formats)
    const date = new Date(trimmed);
    if (!isNaN(date.getTime())) {
      const formatted = new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }).format(date);
      console.log('[HomePage] formatDateDisplay: parsed ' + trimmed + ' → ' + formatted);
      return formatted;
    }
    
    console.warn('[HomePage] formatDateDisplay: could not parse: ' + trimmed);
    return trimmed;
    
  } catch (e) {
    console.error('[HomePage] formatDateDisplay ERROR:', e, 'input=' + dateString);
    return dateString;
  }
}

// ─────────────────────────────────────────────────────────────
// Helper: Transform API CMS data to component format
// NO fallback - ONLY use what comes from database
// ─────────────────────────────────────────────────────────────
function transformCMSToComponent(sections, lang) {
  const result = {
    languageLabel: 'Language',
    heroTaglinePrefix: '',
    heroTaglineAccent: '',
    heroPhilosophy: '',
    quickLinksKicker: '',
    quickLinksTitle: '',
    applyNowTitle: '',
    applyNowText: '',
    schoolCalendarTitle: '',
    schoolCalendarText: '',
    contactUsTitle: '',
    contactUsText: '',
    highlightsKicker: '',
    highlightsTitle: '',
    highlightsIntro: '',
    highlightsLiveLabel: '',
    highlightsNote: '',
    highlightsItems: [],
    newsCountdownKicker: '',
    newsCountdownTitle: '',
    newsCountdownIntro: '',
    latestNewsLabel: '',
    newsItems: [],
    countdownLabel: '',
    countdownTitle: '',
    countdownDateText: '',
    countdownDescription: '',
    countdownDays: '',
    countdownHours: '',
    countdownMinutes: '',
    countdownSeconds: '',
    countdownComplete: '',
  };

  if (!sections || !Array.isArray(sections)) return result;

  sections.forEach((section) => {
    const data = section.content_published || section.content;
    if (!data) return;

    // Extract language-specific data
    const d = (data[lang] && typeof data[lang] === 'object') ? data[lang] : data;

    switch (section.section_type) {
      case 'hero': {
        if (d.tagline_prefix) result.heroTaglinePrefix = d.tagline_prefix;
        if (d.tagline_accent) result.heroTaglineAccent = d.tagline_accent;
        if (d.philosophy) result.heroPhilosophy = d.philosophy;
        // Pass video_file as-is (HeroSection.js will add path prefix)
        if (d.video_file) result._heroVideoUrl = d.video_file;
        break;
      }
      case 'quick_links':
      case 'quicklinks': {
        if (d.kicker) result.quickLinksKicker = d.kicker;
        if (d.title) result.quickLinksTitle = d.title;

        // New CMS format: cards array
        if (Array.isArray(d.cards) && d.cards.length > 0) {
          const [card0 = {}, card1 = {}, card2 = {}] = d.cards;
          if (card0.title) result.applyNowTitle = card0.title;
          if (card0.text) result.applyNowText = card0.text;
          if (card1.title) result.schoolCalendarTitle = card1.title;
          if (card1.text) result.schoolCalendarText = card1.text;
          if (card2.title) result.contactUsTitle = card2.title;
          if (card2.text) result.contactUsText = card2.text;
        } else {
          if (d.apply_now?.title) result.applyNowTitle = d.apply_now.title;
          if (d.apply_now?.text) result.applyNowText = d.apply_now.text;
          if (d.school_calendar?.title) result.schoolCalendarTitle = d.school_calendar.title;
          if (d.school_calendar?.text) result.schoolCalendarText = d.school_calendar.text;
          if (d.contact_us?.title) result.contactUsTitle = d.contact_us.title;
          if (d.contact_us?.text) result.contactUsText = d.contact_us.text;
        }
        break;
      }
      case 'highlights': {
        if (d.kicker) result.highlightsKicker = d.kicker;
        if (d.title) result.highlightsTitle = d.title;
        if (d.intro) result.highlightsIntro = d.intro;
        if (d.live_label || d.liveLabel) result.highlightsLiveLabel = d.live_label || d.liveLabel;
        if (d.live_note || d.liveNote) result.highlightsNote = d.live_note || d.liveNote;
        // ALWAYS overwrite - even if empty array
        result.highlightsItems = Array.isArray(d.items) ? d.items.map(item => ({
          title: item.title || '',
          summary: item.summary || '',
          media_id: item.media_id ?? item.mediaId ?? null,
        })) : [];
        break;
      }
      case 'news': {
        if (d.kicker) result.newsCountdownKicker = d.kicker;
        if (d.title) result.newsCountdownTitle = d.title;
        if (d.intro) result.newsCountdownIntro = d.intro;
        if (d.latest_news_label || d.latestNewsLabel) result.latestNewsLabel = d.latest_news_label || d.latestNewsLabel;
        // ALWAYS overwrite - even if empty array
        result.newsItems = Array.isArray(d.items) ? d.items.map(item => ({
          date: item.date || '',
          title: item.title || '',
          summary: item.summary || '',
          fullContent: item.full_content || item.fullContent || '',
          media_id: item.media_id || item.mediaId || null,
        })) : [];
        break;
      }
      case 'countdown': {
        if (d.countdown_label || d.countdownLabel) result.countdownLabel = d.countdown_label || d.countdownLabel;
        if (d.countdown_title || d.countdownTitle) result.countdownTitle = d.countdown_title || d.countdownTitle;
        if (d.countdown_description || d.countdownDescription) result.countdownDescription = d.countdown_description || d.countdownDescription;
        if (d.countdown_days || d.countdownDays) result.countdownDays = d.countdown_days || d.countdownDays;
        if (d.countdown_hours || d.countdownHours) result.countdownHours = d.countdown_hours || d.countdownHours;
        if (d.countdown_minutes || d.countdownMinutes) result.countdownMinutes = d.countdown_minutes || d.countdownMinutes;
        if (d.countdown_seconds || d.countdownSeconds) result.countdownSeconds = d.countdown_seconds || d.countdownSeconds;
        if (d.countdown_complete || d.countdownComplete) result.countdownComplete = d.countdown_complete || d.countdownComplete;
        
        const rawDate = d.countdown_date || d.countdownDate || d.target_date;
        if (rawDate) {
          try {
            const dt = new Date(rawDate + 'T07:00:00+07:00');
            if (!isNaN(dt.getTime())) {
              result._countdownTimestamp = dt.getTime();
              const languageFormat = lang === 'id' ? 'id-ID' : 'en-US';
              result.countdownDateText = dt.toLocaleDateString(languageFormat, {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              });
            }
          } catch (_) {}
        }
        break;
      }
      default:
        break;
    }
  });

  return result;
}

function HomePage({ language = 'en', onLanguageChange, onNavigate }) {
  const [localLanguage, setLocalLanguage] = useState(language);
  const activeLanguage = onLanguageChange ? language : localLanguage;
  const setLanguage = onLanguageChange || setLocalLanguage;
  
  // CRITICAL OPTIMIZATION: Initialize state with cached data OR null
  // This ensures INSTANT display on first load without waiting for API
  const [cmsSections, setCmsSections] = useState(null); // START WITH NULL - force API fetch
  
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const [apiError, setApiError] = useState(null);
  const [mediaMap, setMediaMap] = useState({});
  const [mediaLoaded, setMediaLoaded] = useState(false);

  // ── Fetch media URLs mapping ────────────────────────────────────────────
  const fetchMediaMap = useCallback(async () => {
    try {
      console.log('[HomePage] Fetching media map...');
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // REDUCED: 5 second timeout
      
      const response = await fetch(
        `${API_BASE}/homepage/cms/media?type=image&_t=${Date.now()}`,
        { 
          signal: controller.signal,
          credentials: 'include',
          headers: {
            'Cache-Control': 'no-cache'
          }
        }
      );
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        console.warn('[HomePage] Media fetch failed:', response.status);
        return;
      }
      
      const result = await response.json();
      
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach(media => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path;
          
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
          }
        });
        
        console.log('[HomePage] ✓ Media map ready:', Object.keys(map).length, 'items');
        setMediaMap(map);
      }
    } catch (err) {
      console.warn('[HomePage] Media fetch error:', err.message);
    } finally {
      setMediaLoaded(true);
    }
  }, []);

  // ── Fetch published CMS content from backend ──────────────────────────────
  const fetchCMSContent = useCallback(async (skipIfCached = false) => {
    try {
      console.log('[HomePage] 🔄 Fetching CMS from API...');
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // REDUCED: 10 second timeout
      
      const response = await fetch(
        `${API_BASE}/homepage/cms/sections?published=true&_t=${Date.now()}`,
        { 
          signal: controller.signal,
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache'
          }
        }
      );
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        console.error('[HomePage] ❌ API error:', response.status);
        setApiError(`HTTP ${response.status}`);
        return;
      }
      
      const result = await response.json();
      
      if (result.ok && Array.isArray(result.data)) {
        console.log('[HomePage] ✅ API SUCCESS - Got', result.data.length, 'sections - NOW RENDERING');
        setCmsSections(result.data);
        setApiError(null);
        
        // Cache untuk next visit
        try {
          localStorage.setItem('homepage_cms_cache', JSON.stringify(result.data));
          localStorage.setItem('homepage_cms_cache_time', Date.now().toString());
        } catch (_) {}
      } else {
        console.error('[HomePage] ❌ Invalid API response:', result);
        setApiError('Invalid response');
      }
    } catch (err) {
      console.error('[HomePage] ❌ fetchCMSContent ERROR:', err.message);
      setApiError(err.message);
    }
  }, []);

  useEffect(() => {
    console.log('[HomePage] ⚡ Mount - check cache FIRST');
    
    // OPTIMIZATION: Try to use cache immediately for instant display
    const cachedData = localStorage.getItem('homepage_cms_cache');
    const cacheTime = localStorage.getItem('homepage_cms_cache_time');
    const CACHE_VALIDITY = 1 * 60 * 1000; // 1 minute (reduced from 5 minutes)
    
    if (cachedData && cacheTime) {
      const age = Date.now() - parseInt(cacheTime);
      if (age < CACHE_VALIDITY) {
        console.log('[HomePage] ✓ Cache is fresh - using immediately');
        try {
          setCmsSections(JSON.parse(cachedData));
        } catch (_) {}
      }
    }
    
    // ALWAYS fetch fresh data in background (don't wait, don't skip)
    console.log('[HomePage] 🔄 Fetching fresh data in background...');
    fetchCMSContent(false);
    fetchMediaMap();
    
    // POLLING: Every 30 seconds refresh (reduced from 60 seconds)
    const pollInterval = setInterval(() => {
      console.log('[HomePage] 🔄 Background poll: Refreshing data');
      fetchCMSContent(false);
      fetchMediaMap();
    }, 30000);
    
    return () => clearInterval(pollInterval);
  }, [fetchCMSContent, fetchMediaMap]);

  // ── Re-fetch when principal approves something (real-time update) ─────────
  useEffect(() => {
    // Listen for storage events (from other tabs or same tab)
    const handleStorageChange = (e) => {
      if (e.key === 'approvalTriggered' && e.newValue === 'true') {
        console.log('[HomePage] ✓ Storage event: approvalTriggered detected');
        console.log('[HomePage] Clearing cache and refetching...');
        // Clear cache to force fresh fetch
        localStorage.removeItem('homepage_cms_cache');
        localStorage.removeItem('homepage_cms_cache_time');
        fetchCMSContent(false);
        fetchMediaMap();
        localStorage.setItem('approvalTriggered', 'false');
      }
      
      // Also check for cms_cache_invalidate flag
      if (e.key === 'cms_cache_invalidate') {
        console.log('[HomePage] ✓ Storage event: cms_cache_invalidate detected');
        console.log('[HomePage] Clearing cache and refetching...');
        localStorage.removeItem('homepage_cms_cache');
        localStorage.removeItem('homepage_cms_cache_time');
        fetchCMSContent(false);
        fetchMediaMap();
      }
    };

    // Listen for custom event dispatch
    const handleApprovalCompleted = (event) => {
      console.log('[HomePage] ✓ approvalCompleted event received');
      console.log('[HomePage] Clearing cache and refetching...');
      // Clear cache to force fresh fetch
      localStorage.removeItem('homepage_cms_cache');
      localStorage.removeItem('homepage_cms_cache_time');
      fetchCMSContent(false);
      fetchMediaMap();
    };

    // Listen for localStorage change from CMS admin panel (same tab or other tab)
    const handleCMSCacheInvalidation = (e) => {
      if (e.key === 'cms_cache_invalidate') {
        console.log('[HomePage] ✓ CMS cache invalidation signal received');
        console.log('[HomePage] Clearing HomePage cache and refetching...');
        localStorage.removeItem('homepage_cms_cache');
        localStorage.removeItem('homepage_cms_cache_time');
        fetchCMSContent(false);
        fetchMediaMap();
      }
    };
    
    window.addEventListener('approvalCompleted', handleApprovalCompleted);
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('storage', handleCMSCacheInvalidation);
    console.log('[HomePage] ✓ Registered approval event listeners');
    
    return () => {
      window.removeEventListener('approvalCompleted', handleApprovalCompleted);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('storage', handleCMSCacheInvalidation);
    };
  }, [fetchCMSContent, fetchMediaMap]);

  // ── Re-fetch when user returns to tab (covers cross-tab approval) ─────────
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const needsRefresh = sessionStorage.getItem('needsCmsRefresh');
        const lastApprovalTime = sessionStorage.getItem('lastApprovalTime');
        
        console.log('[HomePage] Tab regained focus');
        console.log('[HomePage] needsCmsRefresh:', needsRefresh);
        console.log('[HomePage] lastApprovalTime:', lastApprovalTime);
        
        if (needsRefresh === 'true') {
          sessionStorage.removeItem('needsCmsRefresh');
          console.log('[HomePage] ✓ Refetching after tab focus (approval detected)');
          fetchCMSContent();
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    console.log('[HomePage] ✓ Registered visibilitychange event listener');
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      console.log('[HomePage] Unregistered visibilitychange event listener');
    };
  }, [fetchCMSContent]);
  // ─────────────────────────────────────────────────────────────────────────

  // Transform API data ONLY (no hardcoded fallback)
  const copy = transformCMSToComponent(cmsSections, activeLanguage);
  console.log('[HomePage] ✓ Using API data ONLY (no hardcoded content)');
  console.log('[HomePage] Raw _heroVideoUrl:', copy._heroVideoUrl);

  // Use CMS video URL if provided, else local asset
  // IMPORTANT: Video files are in public_html/api/uploads/videos
  // So we need to construct URL with /api prefix
  let heroVideoUrl = null;
  if (copy._heroVideoUrl) {
    // Strip any display prefixes like "Video: " from the filename
    let cleanVideoUrl = copy._heroVideoUrl;
    if (cleanVideoUrl.startsWith('Video: ')) {
      cleanVideoUrl = cleanVideoUrl.replace('Video: ', '');
    }
    // Extract just the filename if it includes full path
    const filename = cleanVideoUrl.includes('/') 
      ? cleanVideoUrl.split('/').pop() 
      : cleanVideoUrl;
    // Construct URL to uploads folder (with /api prefix)
    // Use window.location.origin to get the base domain
    const baseUrl = window.location.origin;
    heroVideoUrl = `${baseUrl}/api/uploads/videos/${filename}`;
  }
  console.log('[HomePage] Final heroVideoUrl:', heroVideoUrl);

  // Use CMS countdown timestamp if provided, else default to 2026-07-13
  const openingDayTime = copy._countdownTimestamp || new Date('2026-07-13T07:00:00+07:00').getTime();

  const highlightItems = copy.highlightsItems || [];

  useEffect(() => {
    if (!onLanguageChange && language !== localLanguage) {
      setLocalLanguage(language);
    }
  }, [language, onLanguageChange, localLanguage]);

  useEffect(() => {
    const dayIndex = new Date().getDay();
    setHighlightIndex(dayIndex % Math.max(1, highlightItems.length));

    const intervalId = setInterval(() => {
      setHighlightIndex((currentIndex) => (currentIndex + 1) % Math.max(1, highlightItems.length));
    }, 5000);

    return () => clearInterval(intervalId);
  }, [highlightItems.length, activeLanguage]);

  useEffect(() => {
    const tickerId = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tickerId);
  }, []);

  // Get image URL from mediaMap based on media_id
  // Handles both database IDs and uploaded filenames
  const getImageForHighlight = (index) => {
    if (!highlightItems || highlightItems.length === 0) return '';
    const item = highlightItems[index % highlightItems.length];
    
    if (!item || !item.media_id) {
      return '';
    }

    // First check mediaMap (for database-referenced images)
    if (mediaMap[item.media_id]) {
      return mediaMap[item.media_id];
    }
    
    const mediaStr = String(item.media_id);
    
    // If it's already a full URL (starts with http)
    if (mediaStr.startsWith('http')) {
      return mediaStr;
    }
    
    // If it starts with /, it's already a path
    if (mediaStr.startsWith('/')) {
      return mediaStr;
    }
    
    // If it contains a dot, it's likely a filename (e.g., media_123.png)
    // Build full URL path
    if (mediaStr.includes('.')) {
      return `/api/uploads/images/${mediaStr}`;
    }
    
    return '';
  };

  const activeHighlight = highlightItems[highlightIndex] || { title: '', summary: '' };
  const activeHighlightPhoto = getImageForHighlight(highlightIndex);
  
  useEffect(() => {
    console.log('[HomePage] RENDER DIAGNOSTICS:');
    console.log('[HomePage]   mediaMap keys:', Object.keys(mediaMap).length);
    console.log('[HomePage]   activeHighlightPhoto:', activeHighlightPhoto ? activeHighlightPhoto.substring(0, 50) + '...' : 'none');
    console.log('[HomePage]   highlightIndex:', highlightIndex);
    console.log('[HomePage]   mediaMap keys:', Object.keys(mediaMap));
  }, [activeHighlightPhoto, highlightIndex, mediaMap]);

  const countdownRemaining = Math.max(0, openingDayTime - now);
  const countdownDays = Math.floor(countdownRemaining / (1000 * 60 * 60 * 24));
  const countdownHours = Math.floor((countdownRemaining / (1000 * 60 * 60)) % 24);
  const countdownMinutes = Math.floor((countdownRemaining / (1000 * 60)) % 60);
  const countdownSeconds = Math.floor((countdownRemaining / 1000) % 60);
  const isOpeningDayReached = countdownRemaining === 0;

  // CRITICAL: Render IMMEDIATELY when cmsSections is ready (don't wait for media)
  // Media images will load in background and appear as soon as available
  if (!cmsSections || cmsSections.length === 0) {
    return (
      <div className="homepage">
        <section className="hero-section hero-fullscreen" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center', color: '#fff' }}>
            <h2>Loading...</h2>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="homepage">
      <section className="hero-section hero-fullscreen">
        <div className="hero-language-switcher" aria-label="Language switcher">
          <span className="hero-language-label">{copy.languageLabel}</span>
          <div className="hero-language-options" role="group" aria-label="Select page language">
            <button
              type="button"
              className={`hero-language-button ${activeLanguage === 'en' ? 'active' : ''}`}
              onClick={() => setLanguage('en')}
            >
              EN
            </button>
            <button
              type="button"
              className={`hero-language-button ${activeLanguage === 'id' ? 'active' : ''}`}
              onClick={() => setLanguage('id')}
            >
              ID
            </button>
          </div>
        </div>

        <div className="hero-video-bg" aria-hidden="true">
          <video 
            className="hero-video-element" 
            autoPlay 
            loop 
            muted 
            playsInline 
            preload="auto"
            key={heroVideoUrl || heroMainVideo}
            onError={(e) => {
              console.error('[HomePage] Video error:', e.target.error);
              console.error('[HomePage] Video error code:', e.target.error?.code);
              console.error('[HomePage] Video error message:', e.target.error?.message);
              console.error('[HomePage] Video src:', heroVideoUrl || heroMainVideo);
            }}
            onLoadStart={() => console.log('[HomePage] Video load start:', heroVideoUrl || heroMainVideo)}
            onCanPlay={() => console.log('[HomePage] Video can play')}
            onLoadedData={() => console.log('[HomePage] Video loaded data')}
            onWaiting={() => console.log('[HomePage] Video waiting')}
            onPlaying={() => console.log('[HomePage] Video playing')}
          >
            <source src={heroVideoUrl || heroMainVideo} type="video/mp4" />
          </video>
        </div>
        <div className="hero-overlay" aria-hidden="true" />
        <div className="hero-content hero-content-layout">
          <article className="hero-message-center">
            <p className="hero-tagline">
              {copy.heroTaglinePrefix} <span className="hero-tagline-accent">{copy.heroTaglineAccent}</span>
            </p>
            <p className="hero-philosophy">
              {copy.heroPhilosophy}
            </p>
          </article>
        </div>
      </section>

      <section className="quick-links-section" aria-label="Quick Links">
        <div className="quick-links-shell">
          <div className="quick-links-header">
            <p className="quick-links-kicker">{copy.quickLinksKicker}</p>
            <h2 className="quick-links-title">{copy.quickLinksTitle}</h2>
          </div>

          <div className="quick-links-grid">
            <button
              className="quick-link-card"
              type="button"
              onClick={() => onNavigate && onNavigate('register')}
            >
              <div className="shimmer-effect"></div>
              <div className="quick-link-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14,2 14,8 20,8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10,9 9,9 8,9"/>
                </svg>
              </div>
              <span className="quick-link-label">01</span>
              <h3>{copy.applyNowTitle}</h3>
              <p>{copy.applyNowText}</p>
            </button>

            <a className="quick-link-card" href="#school-calendar">
              <div className="shimmer-effect"></div>
              <div className="quick-link-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                  <path d="M8 14h.01"/>
                  <path d="M12 14h.01"/>
                  <path d="M16 14h.01"/>
                  <path d="M8 18h.01"/>
                  <path d="M12 18h.01"/>
                </svg>
              </div>
              <span className="quick-link-label">02</span>
              <h3>{copy.schoolCalendarTitle}</h3>
              <p>{copy.schoolCalendarText}</p>
            </a>

            <button className="quick-link-card" onClick={() => {
              // Simulate clicking the Contact us button in navbar
              const contactButton = document.querySelector('.nav-cta');
              if (contactButton) {
                contactButton.click();
              }
            }}>
              <div className="shimmer-effect"></div>
              <div className="quick-link-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
              </div>
              <span className="quick-link-label">03</span>
              <h3>{copy.contactUsTitle}</h3>
              <p>{copy.contactUsText}</p>
            </button>
          </div>
        </div>
      </section>

      <section className="highlights-section" aria-label="Highlights">
        <div className="highlights-shell">
          <div className="highlights-header">
            <p className="highlights-kicker">{copy.highlightsKicker}</p>
            <h2 className="highlights-title">{copy.highlightsTitle}</h2>
            <p className="highlights-intro">{copy.highlightsIntro}</p>
          </div>

          <div className="highlights-grid">
            <article className="highlight-feature-card">
              <div className="highlight-feature-image-wrap">
                <img
                  src={activeHighlightPhoto}
                  alt={activeHighlight.title}
                  className="highlight-feature-image"
                />
                <div className="highlight-image-overlay"></div>
              </div>
              <div className="highlight-feature-content">
                <div className="highlight-feature-topline">
                  <span className="highlight-live-pill">
                    <span className="live-dot"></span>
                    {copy.highlightsLiveLabel}
                  </span>
                  <span className="highlight-live-note">{copy.highlightsNote}</span>
                </div>
                <h3>{activeHighlight.title}</h3>
                <p>{activeHighlight.summary}</p>
                <div className="highlight-progress" aria-hidden="true">
                  {highlightItems.map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      className={`highlight-progress-dot ${index === highlightIndex ? 'active' : ''}`}
                      onClick={() => setHighlightIndex(index)}
                      aria-label={`View highlight ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </article>

            <div className="highlight-news-list-wrapper">
              <div className="highlight-news-list" aria-label="More highlights">
                {highlightItems.map((item, index) => (
                  <article 
                    key={item.title} 
                    className={`highlight-news-item ${index === highlightIndex ? 'active' : ''}`}
                    onClick={() => setHighlightIndex(index)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setHighlightIndex(index);
                      }
                    }}
                  >
                    <div className="highlight-news-item-header">
                      <span className="highlight-news-index">0{index + 1}</span>
                      <svg className="highlight-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </div>
                    <img
                      src={getImageForHighlight(index)}
                      alt={item.title}
                      className="highlight-news-thumb"
                    />
                    <div className="highlight-news-content">
                      <h3>{item.title}</h3>
                      <p>{item.summary}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="news-countdown-section" aria-label="News and countdown">
        <div className="news-countdown-shell">
          <div className="news-countdown-header">
            <p className="news-countdown-kicker">{copy.newsCountdownKicker}</p>
            <h2 className="news-countdown-title">{copy.newsCountdownTitle}</h2>
            <p className="news-countdown-intro">{copy.newsCountdownIntro}</p>
          </div>

          <div className="news-countdown-grid">
            <article className="news-panel-card">
              <div className="news-panel-header">
                <h3 className="news-panel-title">{copy.latestNewsLabel}</h3>
                <button 
                  className="news-see-all-btn"
                  onClick={() => {
                    window.location.hash = '#news-all';
                    window.location.reload();
                  }}
                >
                  <span>{activeLanguage === 'en' ? 'See All' : 'Lihat Semua'}</span>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </button>
              </div>
              <div className="news-list">
                {copy.newsItems.map((item, index) => {
                  // Get image URL using multi-stage resolution (like highlights)
                  const getNewsImageUrl = (mediaId) => {
                    if (!mediaId) return '';
                    
                    // First check mediaMap (for database-referenced images)
                    if (mediaMap[mediaId]) {
                      return mediaMap[mediaId];
                    }
                    
                    const mediaStr = String(mediaId);
                    
                    // If it's already a full URL (starts with http)
                    if (mediaStr.startsWith('http')) {
                      return mediaStr;
                    }
                    
                    // If it starts with /, it's already a path
                    if (mediaStr.startsWith('/')) {
                      return mediaStr;
                    }
                    
                    // If it contains a dot, it's likely a filename
                    // Build full URL path
                    if (mediaStr.includes('.')) {
                      return `/api/uploads/images/${mediaStr}`;
                    }
                    
                    return '';
                  };
                  
                  const newsImageUrl = getNewsImageUrl(item.media_id);
                  
                  // Debug: Log date formatting
                  if (index === 0) {
                    console.log('[HomePage] First news item date raw:', item.date);
                    console.log('[HomePage] First news item date formatted:', formatDateDisplay(item.date));
                  }
                  
                  return (
                    <article 
                      key={item.title} 
                      className="news-item-card"
                      onClick={() => {
                        window.location.hash = `#news/${index + 1}`;
                        window.location.reload();
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          window.location.hash = `#news/${index + 1}`;
                          window.location.reload();
                        }
                      }}
                    >
                      <img
                        src={newsImageUrl}
                        alt={item.title}
                        className="news-item-image"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <div>
                        <p className="news-item-date">{formatDateDisplay(item.date)}</p>
                        <h4>{item.title}</h4>
                        <p>{item.summary}</p>
                      </div>
                    </article>
                  );
                })}
              </div>
            </article>

            <article className="countdown-panel-card">
              <p className="countdown-label">{copy.countdownLabel}</p>
              <h3>{copy.countdownTitle}</h3>
              <p className="countdown-date-text">{copy.countdownDateText}</p>
              <p className="countdown-description">{copy.countdownDescription}</p>

              {isOpeningDayReached ? (
                <p className="countdown-complete">{copy.countdownComplete}</p>
              ) : (
                <div className="countdown-grid" aria-label="Opening day countdown values">
                  <div className="countdown-value-box">
                    <strong>{String(countdownDays).padStart(2, '0')}</strong>
                    <span>{copy.countdownDays}</span>
                  </div>
                  <div className="countdown-value-box">
                    <strong>{String(countdownHours).padStart(2, '0')}</strong>
                    <span>{copy.countdownHours}</span>
                  </div>
                  <div className="countdown-value-box">
                    <strong>{String(countdownMinutes).padStart(2, '0')}</strong>
                    <span>{copy.countdownMinutes}</span>
                  </div>
                  <div className="countdown-value-box">
                    <strong>{String(countdownSeconds).padStart(2, '0')}</strong>
                    <span>{copy.countdownSeconds}</span>
                  </div>
                </div>
              )}
            </article>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;


